/**
 * One tile of the viewport grid: a NiiVue canvas plus the chrome the mockup
 * draws over it — plane header with a dropdown, edge orientation letters and a
 * scale bar.
 *
 * The chrome is DOM rather than NiiVue's built-in text and ruler so it matches
 * the application's typography and can be switched off from the Inspector's
 * "Show Labels" / "Show Scale" checkboxes. The 3D pane is NiiVue volume
 * rendering (`sliceTypeRender`), not a surface mesh — OpenADS does not write
 * meshes.
 */

import * as React from "react";
import { ChevronDown } from "lucide-react";
import { NiiVueCanvas } from "./NiiVueCanvas";
import { PLANE_EDGE_LETTERS, PLANE_LABELS } from "./types";
import type { LocationInfo, ViewPlane, ViewerStatus, ViewerStatsPayload } from "./types";

export interface PaneOption {
  value: string;
  label: string;
}

export interface ViewportPaneProps {
  id: string;
  plane: ViewPlane;
  title?: string;
  baseUrl: string | null;
  overlayUrl: string | null;
  baseColormap: string;
  overlayColormap: string;
  overlayOpacity: number;
  overlayThreshold: { min: number; max: number } | null;
  crosshair: boolean;
  linked: boolean;
  showLabels: boolean;
  showScale: boolean;
  /** Options for the header dropdown; planes normally, volumes in compare mode. */
  headerOptions: PaneOption[];
  headerValue: string;
  onHeaderChange: (value: string) => void;
  onLocationChange?: (loc: LocationInfo) => void;
  onStats?: (stats: ViewerStatsPayload) => void;
}

/** Preferred bar lengths, in mm. 50 first because the mockup asks for "50 mm". */
const SCALE_CANDIDATES_MM = [50, 20, 10, 100, 5];

export function ViewportPane({
  id,
  plane,
  title,
  baseUrl,
  overlayUrl,
  baseColormap,
  overlayColormap,
  overlayOpacity,
  overlayThreshold,
  crosshair,
  linked,
  showLabels,
  showScale,
  headerOptions,
  headerValue,
  onHeaderChange,
  onLocationChange,
  onStats,
}: ViewportPaneProps) {
  const [mmPerPixel, setMmPerPixel] = React.useState<number | null>(null);
  const [status, setStatus] = React.useState<ViewerStatus>("empty");

  const [top, bottom, left, right] = PLANE_EDGE_LETTERS[plane];
  const is3d = plane === "render";

  return (
    <div className="relative flex min-h-0 min-w-0 flex-col overflow-hidden border border-ads-border-strong bg-ads-viewer">
      <PaneHeader
        title={title ?? PLANE_LABELS[plane]}
        options={headerOptions}
        value={headerValue}
        onChange={onHeaderChange}
      />

      <div className="relative min-h-0 flex-1">
        <NiiVueCanvas
          id={id}
          baseUrl={baseUrl}
          overlayUrl={overlayUrl}
          sliceType={plane}
          colormap={baseColormap}
          overlayColormap={overlayColormap}
          opacity={overlayOpacity}
          overlayThreshold={overlayThreshold}
          crosshair={crosshair}
          linked={linked}
          onLocationChange={onLocationChange}
          onStats={onStats}
          onStatusChange={setStatus}
          onScaleChange={setMmPerPixel}
        />

        {!is3d && showLabels && status === "ready" ? (
          <div className="pointer-events-none absolute inset-0 text-[11px] font-medium text-neutral-200">
            <span className="absolute left-1/2 top-1 -translate-x-1/2">{top}</span>
            <span className="absolute bottom-1 left-1/2 -translate-x-1/2">{bottom}</span>
            <span className="absolute left-1.5 top-1/2 -translate-y-1/2">{left}</span>
            <span className="absolute right-1.5 top-1/2 -translate-y-1/2">{right}</span>
          </div>
        ) : null}

        {!is3d && showScale ? <ScaleBar mmPerPixel={mmPerPixel} /> : null}
      </div>
    </div>
  );
}

function PaneHeader({
  title,
  options,
  value,
  onChange,
}: {
  title: string;
  options: PaneOption[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="relative z-10 flex h-6 shrink-0 items-center gap-1 bg-ads-viewer/90 px-2">
      <span className="text-[11px] font-semibold text-white">{title}</span>
      <ChevronDown className="h-3 w-3 text-ads-faint" aria-hidden />
      {/* The visible label is the text above; the select sits on top of it so the
          whole header reads as one dropdown, as in the mockup. */}
      <select
        aria-label={`${title} — change pane contents`}
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * A true scale bar: the length in pixels is derived from the field of view
 * NiiVue reports for the tile it actually drew. When that is unknown (no
 * volume, or WebGL failed) the bar is shown unlabelled rather than guessed.
 */
function ScaleBar({ mmPerPixel }: { mmPerPixel: number | null }) {
  const [height, setHeight] = React.useState(0);
  const ref = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => setHeight(el.clientHeight));
    observer.observe(el);
    setHeight(el.clientHeight);
    return () => observer.disconnect();
  }, []);

  let lengthMm: number | null = null;
  let lengthPx = 0;
  if (mmPerPixel && mmPerPixel > 0 && height > 0) {
    const maxPx = height * 0.45;
    for (const candidate of SCALE_CANDIDATES_MM) {
      if (candidate / mmPerPixel <= maxPx) {
        lengthMm = candidate;
        lengthPx = candidate / mmPerPixel;
        break;
      }
    }
  }

  return (
    <div
      ref={ref}
      className="pointer-events-none absolute inset-y-0 right-4 flex flex-col items-end justify-center"
    >
      {lengthMm ? (
        <>
          <div
            className="relative w-3 border-y border-r border-neutral-300"
            style={{ height: `${lengthPx}px` }}
          >
            {[0.25, 0.5, 0.75].map((f) => (
              <span
                key={f}
                className="absolute right-0 h-px w-1.5 bg-neutral-300"
                style={{ top: `${f * 100}%` }}
              />
            ))}
          </div>
          <span className="mt-1 text-[10px] text-neutral-300">{lengthMm} mm</span>
        </>
      ) : (
        <span className="text-[10px] text-neutral-600">scale unavailable</span>
      )}
    </div>
  );
}
